/**
 * @module Store
 * @remarks Redux store configuration with persistence and middleware setup
 */

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import { authReducer, logout } from "@store/features/authSlice";
import { tokenReducer } from "@store/features/tokenSlice";
import { ninoxAuthService } from "@store/services/ninoxAuthService";
import authMiddleware from "@store/middleware/auth.middleware";
import { setPasswordReducer } from "@store/features/setPasswordSlice";
import passwordRecoveryReducer from "@store/features/passwordRecoverySlice";
import { ninoxApiService } from "@store/services/ninoxApiService";
import dataReducer from "@store/features/dataSlice";
import userReducer from "@store/features/userSlice";
import routingReducer from "@store/features/routingSlice";
import styleSchemaReducer from "@store/features/styleSchemaSlice";
import userActionsReducer from "@store/features/userActionsSlice";
import konamiReducer from "@store/features/konamiSlice";
import missingCustomerReducer from "@store/features/missingCustomerSlice";
import cookieConsentReducer from "@store/features/cookieConsentSlice";
import { customerRevenueApi } from "@store/api-helpers/customerRevenueApi";
import { userApi } from "@store/api-helpers/userApi";
import { PromotionApi } from "@store/api-helpers/promotionsApi";
import { CustomerPromotionApi } from "@store/api-helpers/customerPromotionsApi";
import { createCustomerApi } from "@store/api-helpers/createCustomerApi";
import { updateCustomerApi } from "@store/api-helpers/updateCustomerApi";
import { updateCustomerCardApi } from "@store/api-helpers/updateCustomerCardApi";
import { receiptServiceApi } from "@store/api-helpers/receiptServiceApi";
import { uploadServiceApi } from "@store/api-helpers/uploadServiceApi";
import { FilialenApi } from "@store/api-helpers/filialenApi";
import { PromotionActivationApi } from "@store/api-helpers/promotionsActivationApi";
import { consentsApi } from "@store/api-helpers/consentsApi";
import { activateExistingAccountCompleteApi } from "@store/api-helpers/activateExistingAccountCompleteApi";
import criticalDataReducer from "@store/features/criticalDataSlice";

/**
 * @remarks Combined root reducer including all feature reducers
 */
const rootReducer = combineReducers({
  auth: authReducer,
  token: tokenReducer,
  data: dataReducer,
  user: userReducer,
  userActions: userActionsReducer,
  cookieConsent: cookieConsentReducer,
  [ninoxApiService.reducerPath]: ninoxApiService.reducer,
  [ninoxAuthService.reducerPath]: ninoxAuthService.reducer,
  [customerRevenueApi.reducerPath]: customerRevenueApi.reducer,
  [PromotionApi.reducerPath]: PromotionApi.reducer,
  [CustomerPromotionApi.reducerPath]: CustomerPromotionApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [createCustomerApi.reducerPath]: createCustomerApi.reducer,
  [updateCustomerApi.reducerPath]: updateCustomerApi.reducer,
  [updateCustomerCardApi.reducerPath]: updateCustomerCardApi.reducer,
  [receiptServiceApi.reducerPath]: receiptServiceApi.reducer,
  [uploadServiceApi.reducerPath]: uploadServiceApi.reducer,
  [FilialenApi.reducerPath]: FilialenApi.reducer,
  [PromotionActivationApi.reducerPath]: PromotionActivationApi.reducer,
  [consentsApi.reducerPath]: consentsApi.reducer,
  [activateExistingAccountCompleteApi.reducerPath]: activateExistingAccountCompleteApi.reducer,
  setPassword: setPasswordReducer,
  passwordRecovery: passwordRecoveryReducer,
  routing: routingReducer,
  styleSchema: styleSchemaReducer,
  konami: konamiReducer,
  missingCustomer: missingCustomerReducer,
  criticalData: criticalDataReducer,
});

/**
 * @remarks Configuration for Redux persist
 * Specifies which parts of the state should be persisted
 */
const persistConfig = {
  key: "root",
  version: 1,
  storage,
  whitelist: ["auth", "user", "konami", "cookieConsent", "userApi"], // userApi hinzugefügt für persistente Promotion-Daten
};

/**
 * @remarks Root Reducer Wrapper to reset the entire state on logout
 */
const appReducer = (state: any, action: any) => {
  // When logout action is dispatched, reset all state
  if (action.type === 'auth/logout') {
    // Reset Redux State für alle Reducer außer styleSchema und einige wichtige Einstellungen
    const { styleSchema, cookieConsent } = state;
    
    // Zurücksetzen auf den Initialzustand, aber Stil-Einstellungen und Cookie-Consent behalten
    // userApi wird beim Logout gelöscht für Sicherheit
    state = { styleSchema, cookieConsent };
    
    // LocalStorage & SessionStorage werden bereits in der logout-Action gelöscht (clearAllAppData)
  }
  
  // Normale Reducer-Logik für alle anderen Actions
  return rootReducer(state, action);
};

/**
 * @remarks Persisted reducer wrapping the root reducer
 * Handles saving and rehydrating the specified state slices
 */
const persistedReducer = persistReducer(persistConfig, appReducer);

/**
 * @remarks Redux store instance with configured reducers and middleware
 */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(ninoxAuthService.middleware)
      .concat(ninoxApiService.middleware)
      .concat(customerRevenueApi.middleware)
      .concat(PromotionApi.middleware)
      .concat(CustomerPromotionApi.middleware)
      .concat(userApi.middleware)
      .concat(createCustomerApi.middleware)
      .concat(updateCustomerApi.middleware)
      .concat(updateCustomerCardApi.middleware)
      .concat(receiptServiceApi.middleware)
      .concat(uploadServiceApi.middleware)
      .concat(FilialenApi.middleware)
      .concat(PromotionActivationApi.middleware)
      .concat(consentsApi.middleware)
      .concat(activateExistingAccountCompleteApi.middleware)
      .concat(authMiddleware)
});

// Set up listeners for RTK-Query
setupListeners(store.dispatch);

/**
 * @remarks Redux persistor instance for managing persisted state
 */
export const persistor = persistStore(store);

/**
 * @remarks Type definition for the complete Redux state tree
 */
export type RootState = ReturnType<typeof store.getState>;

/**
 * @remarks Type definition for the store's dispatch function.
 * Includes all action creators and thunks
 */
export type AppDispatch = typeof store.dispatch;

/**
 * @remarks Export logout action for global usage
 */
export { logout };